import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useScrollTrigger,
  Slide,
  Divider,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Avatar,
  ListItemIcon,
  Collapse,
  alpha,
  Fade
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExploreIcon from '@mui/icons-material/Explore';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import ArticleIcon from '@mui/icons-material/Article';
import HelpIcon from '@mui/icons-material/Help';
import InfoIcon from '@mui/icons-material/Info';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import ExpandLess from '@mui/icons-material/ExpandLess';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

// Import login components
import Login from './Login';

// Import our new responsive hook
import useResponsiveSize from '../hooks/useResponsiveSize';

// Hide AppBar on scroll down
function HideOnScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [resourcesAnchorEl, setResourcesAnchorEl] = useState(null);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, userProfile, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  
  // Use our new responsive hook
  const { fontSize, spacing, iconSize, borderRadius } = useResponsiveSize();

  // Debug admin access
  useEffect(() => {
    console.log('Header auth state:', { 
      user: !!user,
      userProfile,
      isAdmin,
      role: userProfile?.role
    });
  }, [user, userProfile, isAdmin]);
  
  // Check if we're on the homepage
  const isHomePage = location.pathname === '/';
  
  // Handle scroll event to change AppBar style
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 30) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };
  
  // Handle Resources menu open/close
  const handleResourcesClick = (event) => {
    setResourcesAnchorEl(event.currentTarget);
  };
  
  const handleResourcesClose = () => {
    setResourcesAnchorEl(null);
  };
  
  const handleMobileResourcesToggle = () => {
    setMobileResourcesOpen(!mobileResourcesOpen);
  };
  
  const handleOpenLoginDialog = () => {
    setLoginDialogOpen(true);
  };
  
  const handleCloseLoginDialog = () => {
    setLoginDialogOpen(false);
  };
  
  const handleSignOut = async () => {
    try {
      setResourcesAnchorEl(null);
      setMobileResourcesOpen(false);
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  // Navigation items (expanded to include all main sections)
  const mainNavItems = [
    { name: 'Plan My Trip', path: '/planner' },
    { name: 'Destinations', path: '/destinations' },
    { name: 'Blog', path: '/blog' },
    ...(user ? [{ name: 'My Trips', path: '/saved-trips' }] : []),
    ...(isAdmin ? [{ name: 'Admin', path: '/admin' }] : [])
  ];
  
  // Footer or other links (not in main navigation)
  const otherLinks = [
    { name: 'Travel Tips', path: '/travel-tips', icon: <TipsAndUpdatesIcon fontSize="small" /> },
    { name: 'FAQs', path: '/faqs', icon: <HelpIcon fontSize="small" /> },
    { name: 'About Us', path: '/about', icon: <InfoIcon fontSize="small" /> },
    { name: 'Contact', path: '/contact', icon: <ContactSupportIcon fontSize="small" /> }
  ];
  
  return (
    <>
      <HideOnScroll>
        <AppBar 
          position="fixed" 
          color="inherit" 
          elevation={isScrolled ? 2 : 0}
          sx={{ 
            backgroundColor: isScrolled 
              ? 'rgba(255, 255, 255, 0.95)' 
              : isHomePage 
                ? 'rgba(255, 255, 255, 0)' 
                : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: isScrolled || !isHomePage ? 'blur(10px)' : 'none',
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            borderBottom: isScrolled ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
            borderRadius: 0,
          }}
        >
          <Container maxWidth="lg">
            <Toolbar 
              disableGutters 
              sx={{ 
                py: { xs: 0.4, md: 0.8 }, // Reduced padding on both mobile and desktop
                minHeight: { xs: '48px', md: '56px' }, // Smaller min-height on both mobile and desktop
              }}
            >
              {/* Logo */}
              <Box 
                component={Link} 
                to="/"
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  textDecoration: 'none',
                  mr: 3,
                }}
              >
                <Avatar 
                  sx={{ 
                    bgcolor: 'primary.main', 
                    width: { xs: 28, md: 32 }, // Smaller avatar on both mobile and desktop
                    height: { xs: 28, md: 32 },
                    mr: 1,
                    background: 'linear-gradient(135deg, #5E17EB 0%, #8352FF 100%)',
                    boxShadow: '0px 4px 10px rgba(94, 23, 235, 0.25)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FlightTakeoffIcon sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }} />
                </Avatar>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="h6"
                    noWrap
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      letterSpacing: '.1rem',
                      backgroundImage: isHomePage && !isScrolled 
                        ? 'linear-gradient(45deg, #fff, #f0f0f0)'
                        : 'linear-gradient(135deg, #5E17EB 0%, #8352FF 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                      WebkitTextFillColor: isHomePage && !isScrolled ? 'white' : 'transparent',
                      textShadow: isHomePage && !isScrolled ? '0 2px 4px rgba(0, 0, 0, 0.2)' : 'none',
                      textDecoration: 'none',
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      mb: -0.3,
                      lineHeight: 1,
                    }}
                  >
                    roamzap
                  </Typography>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      letterSpacing: '.02rem',
                      fontWeight: 700,
                      color: isHomePage && !isScrolled ? 'white' : 'text.secondary',
                      textDecoration: "none",
                      fontSize: { xs: '0.55rem', md: '0.65rem' },
                      mt: 0.2,
                      opacity: 0.9,
                    }}
                  >
                    Travel Plans In a Zap !
                  </Typography>
                </Box>
              </Box>
              
              {/* Desktop Nav Links */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 4 }}>
                {mainNavItems.map((item) => (
                  <Button
                    key={item.name}
                    component={Link}
                    to={item.path}
                    sx={{
                      mx: 1,
                      px: 2,
                      py: 0.75,
                      color: isHomePage && !isScrolled ? 'white' : 'text.primary',
                      fontWeight: 600,
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      position: 'relative',
                      transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      overflow: 'visible',
                      borderRadius: 0,
                      '&::after': location.pathname === item.path ? {
                        content: '""',
                        position: 'absolute',
                        left: '50%',
                        bottom: -6,
                        width: '40%',
                        height: 3,
                        borderRadius: 0,
                        backgroundColor: isHomePage && !isScrolled ? 'white' : 'primary.main',
                        transform: 'translateX(-50%)',
                      } : {},
                      '&:hover': {
                        backgroundColor: 'transparent',
                        transform: 'translateY(-3px)',
                      },
                      textShadow: isHomePage && !isScrolled ? '0 2px 4px rgba(0, 0, 0, 0.2)' : 'none',
                    }}
                  >
                    {item.name}
                  </Button>
                ))}
              </Box>
              
              <Box sx={{ flexGrow: 1 }} />
              
              {/* Desktop User Info */}
              {user ? (
                <Box sx={{ 
                  display: { xs: 'none', md: 'flex' }, 
                  alignItems: 'center',
                  gap: 1
                }}>
                  {user.photoURL ? (
                    <Avatar 
                      src={user.photoURL}
                      alt={user.displayName || 'User'}
                      imgProps={{ 
                        referrerPolicy: "no-referrer",
                        crossOrigin: "anonymous",
                        onError: (e) => {
                          console.error('Avatar image failed to load:', user.photoURL);
                          e.target.onerror = null;
                          e.target.src = '';
                        }
                      }}
                      sx={{ 
                        width: 30,
                        height: 30,
                        border: '2px solid',
                        borderColor: isHomePage && !isScrolled ? 'white' : 'primary.main'
                      }}
                    />
                  ) : (
                    <Avatar 
                      sx={{ 
                        width: 30,
                        height: 30,
                        bgcolor: theme.palette.primary.main,
                        border: '2px solid',
                        borderColor: isHomePage && !isScrolled ? 'white' : 'primary.main'
                      }}
                    >
                      {user.displayName ? user.displayName.charAt(0).toUpperCase() : 
                       user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                  )}
                  <Typography 
                    variant="body2"
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: isHomePage && !isScrolled ? 'white' : 'text.primary'
                    }}
                  >
                    {user.displayName || (user.email && user.email.split('@')[0]) || 'User'}
                  </Typography>
                  <IconButton
                    onClick={handleSignOut}
                    size="small"
                    sx={{ 
                      color: isHomePage && !isScrolled ? 'white' : 'text.secondary',
                      '&:hover': {
                        color: 'error.main'
                      }
                    }}
                  >
                    <LogoutIcon sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleOpenLoginDialog}
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    py: 0.8,
                    px: 2,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Sign In
                </Button>
              )}
              
              {/* Mobile User Info */}
              {user && (
                <Box 
                  sx={{ 
                    display: { xs: 'flex', md: 'none' }, 
                    alignItems: 'center',
                    mr: 1
                  }}
                >
                  {user.photoURL ? (
                    <Avatar 
                      src={user.photoURL}
                      alt={user.displayName || 'User'}
                      imgProps={{ 
                        referrerPolicy: "no-referrer",
                        crossOrigin: "anonymous",
                        onError: (e) => {
                          console.error('Avatar image failed to load:', user.photoURL);
                          e.target.onerror = null;
                          e.target.src = '';
                        }
                      }}
                      sx={{ 
                        width: 28,
                        height: 28,
                        border: '2px solid',
                        borderColor: isHomePage && !isScrolled ? 'white' : 'primary.main'
                      }}
                    />
                  ) : (
                    <Avatar 
                      sx={{ 
                        width: 28,
                        height: 28,
                        bgcolor: theme.palette.primary.main,
                        border: '2px solid',
                        borderColor: isHomePage && !isScrolled ? 'white' : 'primary.main'
                      }}
                    >
                      {user.displayName ? user.displayName.charAt(0).toUpperCase() : 
                       user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                  )}
                </Box>
              )}
              
              {/* Mobile Menu Button */}
              <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                <IconButton
                  size="small" 
                  onClick={toggleDrawer(true)}
                  color="inherit"
                  sx={{ 
                    color: isHomePage && !isScrolled ? 'white' : 'text.primary',
                    padding: 0.3
                  }}
                >
                  <MenuIcon sx={{ fontSize: '1.3rem' }} />
                </IconButton>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>
      
      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          '& .MuiDrawer-paper': { 
            width: '85%', 
            maxWidth: 360,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          },
        }}
      >
        {/* User Info or Sign In Section */}
        {user ? (
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            {user.photoURL ? (
              <Avatar 
                src={user.photoURL}
                alt={user.displayName || 'User'}
                imgProps={{ 
                  referrerPolicy: "no-referrer",
                  crossOrigin: "anonymous",
                  onError: (e) => {
                    console.error('Avatar image failed to load:', user.photoURL);
                    e.target.onerror = null;
                    e.target.src = '';
                  }
                }}
                sx={{ 
                  width: 32, 
                  height: 32,
                  border: '2px solid',
                  borderColor: 'primary.main'
                }}
              />
            ) : (
              <Avatar sx={{ 
                bgcolor: theme.palette.primary.main,
                width: 32,
                height: 32,
                border: '2px solid',
                borderColor: 'primary.main'
              }}>
                {user.displayName ? user.displayName.charAt(0).toUpperCase() : 
                 user.email ? user.email.charAt(0).toUpperCase() : 'U'}
              </Avatar>
            )}
            <Box sx={{ ml: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600} fontSize={fontSize.normal}>
                {user.displayName || (user.email && user.email.split('@')[0]) || 'User'}
              </Typography>
              {user.email && (
                <Typography variant="caption" color="text.secondary" fontSize={fontSize.tiny}>
                  {user.email}
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ 
            p: 2.5, 
            borderBottom: 1, 
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(94, 23, 235, 0.04) 0%, rgba(131, 82, 255, 0.04) 100%)'
          }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Sign in to access all features
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Save trips, get personalized recommendations, and more.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setDrawerOpen(false);
                handleOpenLoginDialog();
              }}
              startIcon={<PersonIcon />}
              sx={{
                py: 1,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Sign In
            </Button>
          </Box>
        )}

        {/* Navigation Links */}
        <List sx={{ pt: 1.5, flex: 1 }}>
          {mainNavItems.map((item) => (
            <ListItem 
              key={item.name} 
              disablePadding 
              sx={{ mb: 0.5 }}
            >
              <Button
                component={Link}
                to={item.path}
                fullWidth
                onClick={toggleDrawer(false)}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  pl: 2,
                  py: 1,
                  borderRadius: borderRadius.medium,
                  fontWeight: 600,
                  fontSize: fontSize.normal,
                  color: 'text.primary',
                  position: 'relative',
                  backgroundColor: location.pathname === item.path 
                    ? alpha(theme.palette.primary.main, 0.08)
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  }
                }}
              >
                {item.name}
              </Button>
            </ListItem>
          ))}
          
          {/* Sign Out Button */}
          {user && (
            <>
              <Divider sx={{ my: 1 }} />
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <Button
                  fullWidth
                  onClick={async () => {
                    await handleSignOut();
                    setDrawerOpen(false);
                  }}
                  startIcon={<LogoutIcon />}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    pl: 2,
                    py: 1,
                    borderRadius: borderRadius.medium,
                    fontWeight: 600,
                    fontSize: fontSize.normal,
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.04),
                    }
                  }}
                >
                  Sign Out
                </Button>
              </ListItem>
            </>
          )}
        </List>
      </Drawer>
      
      {/* Login Dialog */}
      <Login 
        open={loginDialogOpen} 
        onClose={handleCloseLoginDialog} 
      />
    </>
  );
};

export default Header; 